import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '../layouts/DashboardLayout';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import {
  Volume2, Mic, Settings2, Check, Play, Loader2, Users,
  Sparkles, Monitor, Trash2, RefreshCw, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../context/AuthContext';

const VoiceSettingsPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isDark = theme?.isDark;

  // ── Remote data ───────────────────────────────────────────────────────────
  const [accentGroups, setAccentGroups] = useState([]);
  const [coquiAvailable, setCoquiAvailable] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── Engine selection ──────────────────────────────────────────────────────
  const [ttsEngine, setTtsEngine] = useState('coqui');   // 'browser' | 'coqui'

  // ── AI-Based (Coqui) selection state ─────────────────────────────────────
  const [selectedAccent,  setSelectedAccent]  = useState('IN');
  const [selectedSpeaker, setSelectedSpeaker] = useState('p225');
  const [customName,      setCustomName]      = useState('');

  // ── Shared ────────────────────────────────────────────────────────────────
  const [voiceSpeed, setVoiceSpeed] = useState(1.0);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [saving,  setSaving]  = useState(false);
  const [testing, setTesting] = useState(null);   // speaker ID or 'browser'
  const testAudioRef = useRef(null);

  // ── Post-save generation status (AI mode only) ────────────────────────────
  const [savedSpeaker,       setSavedSpeaker]       = useState(null);
  const [generationStatus,   setGenerationStatus]   = useState(null);
  const [pollInterval,       setPollInterval]       = useState(null);
  const [deletingOldSpeaker, setDeletingOldSpeaker] = useState(null);
  const [deleting,           setDeleting]           = useState(false);

  // ── Mount ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([loadStyles(), loadSettings()]).finally(() => setLoading(false));
    return () => {
      if (testAudioRef.current) testAudioRef.current.pause();
      window.speechSynthesis?.cancel();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop polling when generation complete
  useEffect(() => {
    if (generationStatus?.percent === 100 && pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
  }, [generationStatus, pollInterval]);

  useEffect(() => {
    return () => { if (pollInterval) clearInterval(pollInterval); };
  }, [pollInterval]);

  const loadStyles = async () => {
    try {
      const res = await api.get('/voice-styles');
      setAccentGroups(res.data.accent_groups || []);
      setCoquiAvailable(res.data.coqui_available || false);
    } catch (e) {
      console.error('Failed to load voice styles:', e);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await api.get('/voice-settings');
      const s = res.data;
      if (s.tts_mode)      setTtsEngine(s.tts_mode === 'coqui' ? 'coqui' : 'browser');
      if (s.voice_name)    setSelectedSpeaker(s.voice_name);
      if (s.custom_voice_name) setCustomName(s.custom_voice_name);
      if (s.voice_speed)   setVoiceSpeed(s.voice_speed);
      if (s.voice_accent && ['IN', 'GB', 'US'].includes(s.voice_accent))
        setSelectedAccent(s.voice_accent);
    } catch (e) {
      console.error('Failed to load voice settings:', e);
    }
  };

  // ── Test voice ────────────────────────────────────────────────────────────
  const testCoquiVoice = useCallback(async (speakerId) => {
    // Stop any playing audio
    if (testAudioRef.current) {
      testAudioRef.current.pause();
      if (testAudioRef.current._blobUrl) URL.revokeObjectURL(testAudioRef.current._blobUrl);
      testAudioRef.current = null;
    }
    window.speechSynthesis?.cancel();
    setTesting(speakerId);
    const text = 'Hello, this is a VocaLab voice preview. Welcome to your English language lab.';
    let blobUrl = null;
    try {
      const res = await api.get('/tts/audio', {
        params: { text, speaker: speakerId },
        responseType: 'blob',
      });
      blobUrl = URL.createObjectURL(res.data);
      const audio = new Audio(blobUrl);
      audio._blobUrl = blobUrl;
      audio.playbackRate = Math.max(0.5, Math.min(2.0, voiceSpeed));
      testAudioRef.current = audio;
      audio.onended = () => { setTesting(null); URL.revokeObjectURL(blobUrl); testAudioRef.current = null; };
      audio.onerror = () => { setTesting(null); URL.revokeObjectURL(blobUrl); testAudioRef.current = null; toast.error('Audio playback failed'); };
      await audio.play();
    } catch (err) {
      setTesting(null);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      testAudioRef.current = null;
      toast.error(err?.response?.status === 503 ? 'Server TTS not available' : 'Voice test failed');
    }
  }, [voiceSpeed]);

  const testBrowserVoice = useCallback(() => {
    window.speechSynthesis?.cancel();
    if (testAudioRef.current) { testAudioRef.current.pause(); testAudioRef.current = null; }
    setTesting('browser');
    const utterance = new SpeechSynthesisUtterance(
      'Hello, this is a VocaLab voice preview. Welcome to your English language lab.'
    );
    utterance.rate = Math.max(0.5, Math.min(2.0, voiceSpeed));
    utterance.onend  = () => setTesting(null);
    utterance.onerror = () => { setTesting(null); toast.error('Browser TTS failed'); };
    window.speechSynthesis.speak(utterance);
  }, [voiceSpeed]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const saveSettings = async () => {
    setSaving(true);
    const prevSpeaker = savedSpeaker;
    try {
      const payload = {
        tts_mode:  ttsEngine,
        stt_mode:  'browser',
        voice_speed: voiceSpeed,
      };
      if (ttsEngine === 'coqui') {
        payload.voice_name        = selectedSpeaker;
        payload.custom_voice_name = customName.trim();
        payload.voice_accent      = selectedAccent;
      }
      await api.put('/voice-settings', payload);

      if (ttsEngine === 'coqui') {
        toast.success('AI voice saved. Generating audio for all questions…');
        setSavedSpeaker(selectedSpeaker);
        if (prevSpeaker && prevSpeaker !== selectedSpeaker) setDeletingOldSpeaker(prevSpeaker);
        // Trigger background generation for all existing questions with the new speaker
        try {
          await api.post('/tts/prewarm-teacher', { speaker: selectedSpeaker });
        } catch {}
        startPolling(selectedSpeaker);
      } else {
        toast.success('Voice settings saved. All students will use browser TTS.');
      }
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const startPolling = (speaker) => {
    setPollInterval(prev => { if (prev) clearInterval(prev); return null; });
    setGenerationStatus(null);
    const fetch = async () => {
      try {
        const res = await api.get('/tts/prewarm-status', { params: { speaker } });
        setGenerationStatus(res.data);
      } catch {}
    };
    fetch();
    const id = setInterval(fetch, 2500);
    setPollInterval(id);
  };

  const deleteOldCache = async () => {
    if (!deletingOldSpeaker) return;
    setDeleting(true);
    try {
      const res = await api.delete('/tts/cache', { params: { speaker: deletingOldSpeaker } });
      toast.success(`Deleted ${res.data.deleted} old audio file(s)`);
      setDeletingOldSpeaker(null);
    } catch {
      toast.error('Failed to delete old audio cache');
    } finally {
      setDeleting(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const accentColors = {
    IN: { ring: 'ring-orange-400', badge: 'bg-orange-500/20 text-orange-500', selected: 'border-orange-400 bg-orange-500/10', tab: 'from-orange-500 to-amber-500' },
    GB: { ring: 'ring-indigo-400', badge: 'bg-indigo-500/20 text-indigo-400', selected: 'border-indigo-400 bg-indigo-500/10', tab: 'from-indigo-500 to-purple-500' },
    US: { ring: 'ring-blue-400',   badge: 'bg-blue-500/20 text-blue-400',   selected: 'border-blue-400 bg-blue-500/10',   tab: 'from-blue-500 to-cyan-500' },
  };

  const currentGroup = accentGroups.find(g => g.code === selectedAccent);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
        </div>
      </DashboardLayout>
    );
  }

  const canSave = user?.role === 'teacher';

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">

        {/* Header */}
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Voice Settings
          </h1>
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
            Configure Text-to-Speech for all lab modules
          </p>
        </div>

        {/* Teacher banner */}
        {user?.role === 'teacher' && (
          <div className={`flex items-start gap-3 p-4 rounded-xl border ${isDark ? 'bg-violet-500/10 border-violet-500/30' : 'bg-violet-50 border-violet-200'}`}>
            <Users className="h-5 w-5 text-violet-500 mt-0.5 shrink-0" />
            <div>
              <p className={`font-semibold text-sm ${isDark ? 'text-violet-300' : 'text-violet-800'}`}>Class Voice Settings</p>
              <p className={`text-sm ${isDark ? 'text-violet-400' : 'text-violet-700'}`}>
                Settings saved here apply to all students in your assigned classes.
              </p>
            </div>
          </div>
        )}

        {/* ── TTS Engine picker ──────────────────────────────────────────── */}
        <Card className={isDark ? 'premium-card' : ''}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <Volume2 className="h-5 w-5 text-cyan-500" />
              TTS Engine
            </CardTitle>
            <CardDescription className={isDark ? 'text-slate-400' : ''}>
              Choose between the browser's built-in voice or the high-quality AI neural engine
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Normal — Browser */}
              <button
                onClick={() => setTtsEngine('browser')}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  ttsEngine === 'browser'
                    ? isDark ? 'border-emerald-500 bg-emerald-500/10' : 'border-emerald-500 bg-emerald-50'
                    : isDark ? 'border-white/10 hover:border-white/25' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-2 rounded-lg ${isDark ? 'bg-white/10' : 'bg-slate-100'}`}>
                    <Monitor className="h-5 w-5 text-emerald-500" />
                  </div>
                  {ttsEngine === 'browser' && <Check className="h-5 w-5 text-emerald-500" />}
                </div>
                <p className={`font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Normal</p>
                <p className={`text-xs font-medium mb-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>System Based</p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Professional voices with distinct regional characteristics. Works instantly, no server processing.
                </p>
              </button>

              {/* AI Based — Coqui */}
              <button
                onClick={() => coquiAvailable && setTtsEngine('coqui')}
                disabled={!coquiAvailable}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  !coquiAvailable
                    ? isDark ? 'border-white/5 opacity-50 cursor-not-allowed' : 'border-slate-100 opacity-50 cursor-not-allowed'
                    : ttsEngine === 'coqui'
                      ? isDark ? 'border-violet-500 bg-violet-500/10' : 'border-violet-500 bg-violet-50'
                      : isDark ? 'border-white/10 hover:border-violet-400/50' : 'border-slate-200 hover:border-violet-300'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-2 rounded-lg ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
                    <Sparkles className="h-5 w-5 text-violet-500" />
                  </div>
                  {ttsEngine === 'coqui' && <Check className="h-5 w-5 text-violet-500" />}
                  {!coquiAvailable && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-slate-200 text-slate-500'}`}>
                      Unavailable
                    </span>
                  )}
                </div>
                <p className={`font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>AI Based</p>
                <p className={`text-xs font-medium mb-2 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}>Premium Quality</p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  High-quality AI-powered voices with natural speech patterns. Pre-generated for instant playback.
                </p>
              </button>

            </div>
          </CardContent>
        </Card>

        {/* ── Normal / Browser TTS ───────────────────────────────────────── */}
        {ttsEngine === 'browser' && (
          <Card className={isDark ? 'premium-card' : ''}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                <Monitor className="h-5 w-5 text-emerald-500" />
                Browser Voice
              </CardTitle>
              <CardDescription className={isDark ? 'text-slate-400' : ''}>
                Uses the browser's built-in Web Speech API — no server processing required
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                <ul className={`text-sm space-y-1.5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  <li>• Voice quality depends on the student's browser and operating system</li>
                  <li>• Works offline — no network required for playback</li>
                  <li>• No audio pre-generation needed</li>
                </ul>
              </div>

              {/* Speed */}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label className={isDark ? 'text-white' : ''}>Reading Speed</Label>
                  <span className={`text-sm font-semibold ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`}>{voiceSpeed.toFixed(1)}x</span>
                </div>
                <Slider value={[voiceSpeed]} onValueChange={([v]) => setVoiceSpeed(v)} min={0.5} max={2.0} step={0.1} className="cursor-pointer" />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Slow (0.5×)</span><span>Normal (1.0×)</span><span>Fast (2.0×)</span>
                </div>
              </div>

              {/* Test button */}
              <Button
                onClick={testBrowserVoice}
                disabled={testing !== null}
                variant="outline"
                className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
              >
                {testing === 'browser'
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Playing…</>
                  : <><Play className="h-4 w-4 mr-2" />Test Voice</>
                }
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── AI Based — Accent picker ───────────────────────────────────── */}
        {ttsEngine === 'coqui' && (
          <>
            <Card className={isDark ? 'premium-card' : ''}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                  <ChevronRight className="h-5 w-5 text-violet-500" />
                  Select Accent
                </CardTitle>
                <CardDescription className={isDark ? 'text-slate-400' : ''}>
                  Choose the English accent group for your class
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {accentGroups.map(group => {
                    const c = accentColors[group.code] || accentColors.GB;
                    const isActive = selectedAccent === group.code;
                    return (
                      <button
                        key={group.code}
                        onClick={() => setSelectedAccent(group.code)}
                        className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                          isActive ? c.selected : isDark ? 'border-white/10 hover:border-white/25' : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        {isActive && <span className={`absolute top-2 right-2 h-2 w-2 rounded-full bg-gradient-to-br ${c.tab}`} />}
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-2xl">{group.flag}</span>
                          <span className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{group.code}</span>
                        </div>
                        <p className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{group.name}</p>
                        <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{group.speakers?.length} voices</p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* ── Voice grid ──────────────────────────────────────────────── */}
            {currentGroup && (
              <Card className={isDark ? 'premium-card' : ''}>
                <CardHeader>
                  <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                    <span className="text-xl">{currentGroup.flag}</span>
                    Select Voice — {currentGroup.name}
                  </CardTitle>
                  <CardDescription className={isDark ? 'text-slate-400' : ''}>
                    {currentGroup.speakers?.length} speakers · VCTK corpus neural voices · Click to select, Preview to listen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {currentGroup.speakers?.map(spk => {
                      const c = accentColors[currentGroup.code] || accentColors.GB;
                      const isSelected = selectedSpeaker === spk.id;
                      const isTesting  = testing === spk.id;
                      return (
                        <div
                          key={spk.id}
                          onClick={() => setSelectedSpeaker(spk.id)}
                          className={`relative p-3 rounded-xl border-2 cursor-pointer transition-all ${
                            isSelected ? c.selected : isDark ? 'border-white/10 hover:border-white/25' : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          {isSelected && <Check className={`absolute top-2 right-2 h-4 w-4 ${c.badge.split(' ')[1]}`} />}
                          <p className={`font-mono font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{spk.id}</p>
                          {selectedAccent === 'IN' && (
                            <span className={`inline-block text-xs px-1.5 py-0.5 rounded-full mt-1 ${c.badge}`}>{spk.gender}</span>
                          )}
                          <button
                            onClick={e => { e.stopPropagation(); testCoquiVoice(spk.id); }}
                            disabled={testing !== null}
                            className={`mt-2 w-full flex items-center justify-center gap-1 py-1 rounded-lg text-xs transition-colors ${
                              isTesting
                                ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'
                                : isDark ? 'bg-white/5 hover:bg-white/10 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                            }`}
                          >
                            {isTesting
                              ? <><Loader2 className="h-3 w-3 animate-spin" />Playing</>
                              : <><Play className="h-3 w-3" />Preview</>
                            }
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ── Custom name + Speed ──────────────────────────────────────── */}
            <Card className={isDark ? 'premium-card' : ''}>
              <CardHeader>
                <CardTitle className={`${isDark ? 'text-white' : ''}`}>Voice Name &amp; Speed</CardTitle>
                <CardDescription className={isDark ? 'text-slate-400' : ''}>
                  Optionally label this voice for your records, and set reading speed
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Selected voice badge */}
                {selectedSpeaker && currentGroup && (() => {
                  const spk = accentGroups.flatMap(g => g.speakers).find(s => s.id === selectedSpeaker);
                  if (!spk) return null;
                  const c = accentColors[selectedAccent] || accentColors.GB;
                  return (
                    <div className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                      <span className="text-xl">{currentGroup.flag}</span>
                      <div>
                        <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{spk.id}</p>
                        <div className="flex flex-wrap items-center gap-1 mt-0.5">
                          {selectedAccent === 'IN' && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${c.badge}`}>{spk.gender}</span>
                          )}
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-700'}`}>Neural · VCTK</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${isDark ? 'bg-white/10 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>{currentGroup.name}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Custom name */}
                <div className="space-y-2">
                  <Label className={isDark ? 'text-white' : ''}>
                    Custom Voice Name <span className={`text-xs font-normal ${isDark ? 'text-slate-400' : 'text-slate-400'}`}>(optional)</span>
                  </Label>
                  <Input
                    value={customName}
                    onChange={e => setCustomName(e.target.value)}
                    placeholder={`e.g. "Class Voice", "${selectedSpeaker}"`}
                    className={isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-slate-500' : ''}
                    maxLength={50}
                  />
                </div>

                {/* Speed */}
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <Label className={isDark ? 'text-white' : ''}>Reading Speed</Label>
                    <span className={`text-sm font-semibold ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`}>{voiceSpeed.toFixed(1)}x</span>
                  </div>
                  <Slider value={[voiceSpeed]} onValueChange={([v]) => setVoiceSpeed(v)} min={0.5} max={2.0} step={0.1} className="cursor-pointer" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Slow (0.5×)</span><span>Normal (1.0×)</span><span>Fast (2.0×)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ── STT info ──────────────────────────────────────────────────── */}
        <Card className={isDark ? 'premium-card' : ''}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <Mic className="h-5 w-5 text-pink-500" />
              Speech Recognition
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
              <ul className={`text-sm space-y-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                <li>• Uses Web Speech API (Chrome recommended)</li>
                <li>• Works offline in most browsers</li>
                <li>• Requires microphone permission</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* ── Save ──────────────────────────────────────────────────────── */}
        {canSave && (
          <div className="flex justify-end">
            <Button
              onClick={saveSettings}
              disabled={saving || (ttsEngine === 'coqui' && !selectedSpeaker)}
              className={`bg-gradient-to-r ${theme?.colors?.primary || 'from-cyan-500 to-violet-500'} text-white`}
            >
              {saving
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                : <><Settings2 className="h-4 w-4 mr-2" />Save &amp; Apply to Classes</>
              }
            </Button>
          </div>
        )}

        {/* ── Post-save generation progress ─────────────────────────────── */}
        {generationStatus && ttsEngine === 'coqui' && (
          <Card className={`border-2 ${generationStatus.percent === 100
            ? isDark ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-emerald-400 bg-emerald-50'
            : isDark ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-cyan-300 bg-cyan-50'
          }`}>
            <CardHeader className="pb-3">
              <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : ''}`}>
                {generationStatus.percent === 100
                  ? <Check className="h-5 w-5 text-emerald-500" />
                  : <Loader2 className="h-5 w-5 text-cyan-500 animate-spin" />
                }
                {generationStatus.percent === 100 ? 'Audio Generation Complete' : 'Generating Audio Files…'}
                <button onClick={() => startPolling(savedSpeaker)} className={`ml-auto p-1 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`} title="Refresh">
                  <RefreshCw className="h-4 w-4 text-slate-400" />
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className={`w-full rounded-full h-3 ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${generationStatus.percent === 100 ? 'bg-gradient-to-r from-emerald-400 to-green-500' : 'bg-gradient-to-r from-cyan-400 to-violet-500'}`}
                  style={{ width: `${generationStatus.percent}%` }}
                />
              </div>
              <div className="flex justify-between text-sm">
                <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>{generationStatus.cached} / {generationStatus.total} questions cached</span>
                <span className={`font-bold ${generationStatus.percent === 100 ? isDark ? 'text-emerald-400' : 'text-emerald-600' : isDark ? 'text-cyan-400' : 'text-cyan-700'}`}>{generationStatus.percent}%</span>
              </div>
              {generationStatus.percent === 100 && (
                <p className={`text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
                  All questions are pre-cached — students will get instant audio playback.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Delete old cache ───────────────────────────────────────────── */}
        {deletingOldSpeaker && generationStatus?.percent === 100 && (
          <Card className={`border-2 ${isDark ? 'border-amber-500/30 bg-amber-500/5' : 'border-amber-300 bg-amber-50'}`}>
            <CardContent className="pt-5">
              <div className="flex items-start gap-3">
                <Trash2 className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className={`font-semibold text-sm ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>Old audio files still on disk</p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                    Audio for the previous voice (<span className="font-mono font-bold">{deletingOldSpeaker}</span>) is still cached. Delete to free disk space.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" onClick={deleteOldCache} disabled={deleting} className="bg-amber-500 hover:bg-amber-600 text-white">
                      {deleting ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Deleting…</> : <><Trash2 className="h-3 w-3 mr-1" />Delete Old Files</>}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeletingOldSpeaker(null)} className={isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500'}>Keep Files</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

      </div>
    </DashboardLayout>
  );
};

export default VoiceSettingsPage;
